/**
 * WHO a figure is about, in words (owner, 18 Oct 2026: "you keep bringing it back to just Robert … look
 * at who the goal applies to, don't just assume"; module reviews, 18 Oct: "Where Colin stands today" on
 * Colin and Shelly's file, "Where Joe stands today" on Joe and Kim's).
 *
 * Money figures — the Age Pension, cashflow, net worth, the estate — belong to the HOUSEHOLD, so a couple
 * is always named as both. A goal that the profile records per person ("Who is the goal … for?") is
 * named from that answer instead: pass `forWhom`.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';

export interface HouseholdWords {
    /** "Colin and Shelly", "Colin", or "you". */
    names: string;
    /** True when `names` takes a plural verb: "Colin and Shelly stand", "you stand". */
    plural: boolean;
    /** "Colin and Shelly's", "Colin's", "your". */
    possessive: string;
    /** "stand" / "stands" — the verb agreeing with `names`. */
    stand: string;
}

export function householdWords(factFind: DraftFactFind | null | undefined, forWhom?: string | null): HouseholdWords {
    const client = String(factFind?.client?.firstName || '').trim();
    const partner = String(factFind?.partner?.firstName || '').trim();
    const isCouple = factFind?.adviceFor === 'couple' && !!partner;
    let names: string;
    if (!client) names = 'you';
    else if (!isCouple) names = client;
    else if (forWhom === 'client') names = client;
    else if (forWhom === 'partner') names = partner;
    else names = `${client} and ${partner}`;
    const plural = names === 'you' || names.includes(' and ');
    const possessive = names === 'you' ? 'your' : /s$/i.test(names) ? `${names}'` : `${names}'s`;
    return { names, plural, possessive, stand: plural ? 'stand' : 'stands' };
}

/*
 * WHO IS READING (owner, 21 Oct 2026, village review decision 13): "you" when the client is signed in,
 * the household's names when an adviser opens the file from the partner portal, and names in the advice
 * document either way. The router sets this on every render from the professional case being open.
 */
let adviserViewing = false;
export function setAdviserViewing(value: boolean): void { adviserViewing = value; }
export function isAdviserViewing(): boolean { return adviserViewing; }

/**
 * The household in the reader's voice, with the pronouns to go with it. A screen written with these
 * reads "What matters most to you about moving?" for the client and "What matters most to John and
 * Kerry about moving?" for the adviser, from one sentence. The modules rebuilt on it use this; the rest
 * still use householdWords (names), until their copy is converted.
 */
export interface HouseholdVoice {
    /** True when speaking to the client ("you"). */
    you: boolean;
    /** "you", "John and Kerry", "Robin". */
    names: string;
    /** Sentence start: "You", "John and Kerry". */
    Names: string;
    /** "your", "John and Kerry's". */
    possessive: string;
    Possessive: string;
    /** Pronouns after the first mention: you/they, you/them, your/their. */
    they: string;
    They: string;
    them: string;
    their: string;
    Their: string;
    /** Verbs agreeing with `names`: are/is, have/has, were/was; and with `they` (always plural). */
    are: string;
    have: string;
    /** "-s" for a verb after `names`: `${v.names} keep${v.s}` → "you keep", "Robin keeps". */
    s: string;
    /** True when `names` is two people. */
    couple: boolean;
}

export function householdVoice(factFind: DraftFactFind | null | undefined, forWhom?: string | null): HouseholdVoice {
    const w = householdWords(factFind, forWhom);
    const toClient = !adviserViewing || w.names === 'you';
    if (toClient) {
        return { you: true, names: 'you', Names: 'You', possessive: 'your', Possessive: 'Your', they: 'you', They: 'You', them: 'you', their: 'your', Their: 'Your', are: 'are', have: 'have', s: '', couple: w.names.includes(' and ') };
    }
    const plural = w.plural;
    return {
        you: false, names: w.names, Names: w.names, possessive: w.possessive, Possessive: w.possessive,
        they: 'they', They: 'They', them: 'them', their: 'their', Their: 'Their',
        are: plural ? 'are' : 'is', have: plural ? 'have' : 'has', s: plural ? '' : 's', couple: plural,
    };
}

/*
 * SHARED COPY, TURNED TO THE READER (village rebuild item 8, 21 Oct 2026). Copy written once for every
 * module — the risks, the explainers — speaks to the client ("If you need money in a hurry…"). An adviser
 * reading it about John and Kerry should read "If they need money in a hurry…". For the client this returns
 * the text unchanged. "you" after a verb or preposition is the object ("costs you income" → "costs them
 * income"); anywhere else it is the subject.
 */
const OBJECT_AFTER = /\b(to|for|with|from|costs?|gives?|pays?|tells?|lets?|helps?|leaves?|charges?|owes?|shows?|protects?|suits?)\s+$/i;
export function inReaderWords(text: string, v: HouseholdVoice): string {
    if (v.you || !text) return text;
    return text
        .replace(/\byourselves\b|\byourself\b/g, 'themselves')
        .replace(/\bYours\b/g, 'Theirs').replace(/\byours\b/g, 'theirs')
        .replace(/\bYour\b/g, 'Their').replace(/\byour\b/g, 'their')
        .replace(/\bYou('d|'ll|'re|'ve)?\b/g, (_m, c: string | undefined) => `They${c || ''}`)
        .replace(/\byou('d|'ll|'re|'ve)?\b/g, (_m, c: string | undefined, offset: number, whole: string) =>
            !c && OBJECT_AFTER.test(whole.slice(Math.max(0, offset - 12), offset)) ? 'them' : `they${c || ''}`);
}
